/**
 * Hook React Query para resolver Usuario.id -> Persona slim (0677 F3 piloto multi-modelo #4).
 *
 * Endpoint backend (BACKLOG [0675+][PERSONA-RESOLUTION]):
 *   GET /api/v1/apps/personas/by-user/:userId/chip
 *
 * Cache compartido entre componentes (queryKey ['personaChip', userId] · staleTime 5min).
 * Reusable en AdjuntosSection, ComentariosSection, futuro WorkflowTimeline + VersionTimeline.
 */

import { useQuery } from '@tanstack/react-query';
import { request } from '../services/api';

export interface PersonaChipData {
  id: string;
  nombre: string;
  apellidos: string;
  codigo: string;
  email: string;
  puestoTitulo: string | null;
  puestoCodigo: string | null;
}

const QUERY_KEY = (userId: string) => ['personaChip', userId] as const;

export function usePersona(userId: string | null | undefined, enabled = true) {
  return useQuery({
    queryKey: QUERY_KEY(userId ?? ''),
    queryFn: async (): Promise<PersonaChipData> => {
      const res = await request<{ data: PersonaChipData }>(`/apps/personas/by-user/${userId}/chip`);
      return res.data;
    },
    enabled: enabled && !!userId,
    staleTime: 5 * 60_000, // 5 minutos · personas no cambian frecuentemente
    retry: 1, // 1 retry · si falla 404 (usuario sin persona) no insiste
  });
}

// Helper: nombre completo formateado
export function formatNombreCompleto(p: Pick<PersonaChipData, 'nombre' | 'apellidos'>): string {
  return `${p.nombre} ${p.apellidos}`.trim();
}

// Helper: iniciales para avatar (max 2 chars)
export function getIniciales(p: Pick<PersonaChipData, 'nombre' | 'apellidos'>): string {
  const n = (p.nombre || '').trim()[0] ?? '';
  const a = (p.apellidos || '').trim()[0] ?? '';
  return (n + a).toUpperCase() || '?';
}

// Helper: color determinista del avatar segun UUID (hash → HSL)
export function getColorAvatar(userId: string): string {
  let hash = 0;
  for (let i = 0; i < userId.length; i++) {
    hash = userId.charCodeAt(i) + ((hash << 5) - hash);
    hash = hash & hash;
  }
  const hue = Math.abs(hash) % 360;
  return `hsl(${hue}, 45%, 45%)`; // saturacion + luminosidad fijas para coherencia visual
}
